import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Link as MuiLink,
  Stack,
  useTheme as useMuiTheme,
  alpha,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import ErrorMessage from "../components/common/ErrorMessage";
import StyledCard from "../components/common/StyledCard";
import StyledTextField from "../components/common/StyledTextField";
import StyledButton from "../components/common/StyledButton";

const Login: React.FC = () => {
  const [formData, setFormData] = useState({
    login: "",
    password: "",
  });
  const [formErrors, setFormErrors] = useState({
    login: "",
    password: "",
  });
  const { login, error, clearError } = useAuth();
  const navigate = useNavigate();
  const theme = useMuiTheme();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setFormErrors({ ...formErrors, [name]: "" });
    if (error) clearError();
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = { ...formErrors };

    if (!formData.login.trim()) {
      newErrors.login = "Email or username is required";
      valid = false;
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
      valid = false;
    }

    setFormErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      await login(formData);
      navigate("/dashboard");
    } catch (error) {
      // Error is handled by the auth context
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        backgroundImage:
          theme.palette.mode === "light"
            ? `radial-gradient(circle at 20% 80%, ${alpha(
              theme.palette.primary.main,
              0.05
            )} 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, ${alpha(
              theme.palette.secondary.main,
              0.05
            )} 0%, transparent 50%)`
            : `radial-gradient(circle at 20% 80%, ${alpha(
              theme.palette.primary.main,
              0.08
            )} 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, ${alpha(
              theme.palette.secondary.main,
              0.08
            )} 0%, transparent 50%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: "-50%",
          right: "-10%",
          width: "600px",
          height: "600px",
          borderRadius: "50%",
          background: alpha(theme.palette.primary.main, 0.08),
          filter: "blur(80px)",
          animation: "float 8s ease-in-out infinite",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: "-30%",
          left: "-5%",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          background: alpha(theme.palette.secondary.main, 0.08),
          filter: "blur(80px)",
          animation: "float 10s ease-in-out infinite",
          animationDelay: "2s",
        },
        "@keyframes float": {
          "0%, 100%": {
            transform: "translateY(0px)",
          },
          "50%": {
            transform: "translateY(30px)",
          },
        },
      }}
    >
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            position: "relative",
            zIndex: 1,
            animation: "slideInTop 0.6s ease-out",
            "@keyframes slideInTop": {
              from: {
                opacity: 0,
                transform: "translateY(-30px)",
              },
              to: {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          }}
        >
          <StyledCard
            sx={{
              width: "100%",
              background:
                theme.palette.mode === "light"
                  ? `linear-gradient(145deg, ${alpha(
                    theme.palette.background.paper,
                    0.8
                  )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`
                  : `linear-gradient(145deg, ${alpha(
                    theme.palette.background.paper,
                    0.9
                  )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
              backdropFilter: "blur(20px)",
              p: { xs: 3, sm: 5 },
            }}
          >
            <Stack spacing={4} alignItems="center" sx={{ width: "100%" }}>
              <Box textAlign="center">
                <Typography
                  component="h1"
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    mb: 1.5,
                    letterSpacing: "-0.02em",
                  }}
                >
                  Welcome Back
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Enter your credentials to access your account
                </Typography>
              </Box>

              <ErrorMessage message={error} />

              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{ width: "100%" }}
              >
                <Stack spacing={2.5}>
                  <StyledTextField
                    required
                    fullWidth
                    id="login"
                    label="Email or Username"
                    name="login"
                    autoComplete="email"
                    autoFocus
                    value={formData.login}
                    onChange={handleChange}
                    error={!!formErrors.login}
                    helperText={formErrors.login}
                  />
                  <StyledTextField
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    type="password"
                    id="password"
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleChange}
                    error={!!formErrors.password}
                    helperText={formErrors.password}
                  />
                </Stack>

                <StyledButton
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{
                    mt: 4,
                    mb: 3,
                    py: 1.5,
                    fontSize: "1rem",
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                    "&:hover": {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                      boxShadow: `0 10px 25px ${alpha(theme.palette.primary.main, 0.4)}`,
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  Sign In
                </StyledButton>

                <Grid container justifyContent="center">
                  <Grid item>
                    <MuiLink
                      component={Link}
                      to="/register"
                      variant="body2"
                      sx={{
                        textDecoration: "none",
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                        transition: "color 0.2s",
                        "&:hover": {
                          color: theme.palette.primary.main,
                        },
                      }}
                    >
                      Don't have an account? <span style={{ color: theme.palette.primary.main }}>Sign Up</span>
                    </MuiLink>
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          </StyledCard>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;
